import type { Team } from '@compass/client/types/team';
import { AlertTriangle, Loader2 } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDeleteOrganizationTeam } from '@/hooks/useTeams';
import { getApiErrorMessage } from '@/utils/apiError';

interface DeleteTeamDialogProps {
  organizationId: string;
  team: Team | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DeleteTeamDialog: React.FC<DeleteTeamDialogProps> = ({
  organizationId,
  team,
  open,
  onOpenChange,
}) => {
  const deleteMutation = useDeleteOrganizationTeam();

  const handleDelete = async () => {
    if (!team?.id) return;

    try {
      await deleteMutation.mutateAsync({
        organization_id: organizationId,
        team_id: team.id,
      });

      toast.success(`Team "${team.name}" has been deleted`);
      onOpenChange(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete team'));
    }
  };

  if (!team) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Team
          </DialogTitle>
          <DialogDescription className="pt-4">
            Are you sure you want to delete <span className="font-medium">{team.name}</span>? This
            action cannot be undone.
            {(team.member_ids?.length ?? 0) > 0 && (
              <span className="mt-2 block text-destructive">
                This team has {team.member_ids!.length}{' '}
                {team.member_ids!.length === 1 ? 'member' : 'members'} who will be unlinked.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete Team
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
